import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion, AccordionBody, AccordionHeader, Skeleton, SkeletonBlock
} from '@one-thd/sui-atomic-components';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { AppContext, withDynamicComponent } from '@thd-nucleus/app-render';
import { SpecificationsWithKPFAccordionBodyComponent } from '../composed/SpecificationsWithKPFAccordionBody';
import { dataModel } from '../dataModel';
import { SpecificationsAccordionTitle } from './SpecificationsAccordionTitle';
import { SpecificationsAccordionBodyComponent } from './SpecificationsAccordionBody';
import { isEventBusCreated } from '../helpers';

export const SpecificationsAccordionComponent = (props) => {
  const {
    itemId,
    oneColumn,
    returnableStatus,
    isWhiteSpecTitle,
    showSubtitle,
    withKeyProductFeatures,
    displaySimilarLinks
  } = props;
  const { instance } = useContext(AppContext);
  const thdSeoBotDetected = instance?.thdSEOBotDetection;
  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = (canCloseAccordion = true) => {
    if (canCloseAccordion || (!canCloseAccordion && expanded)) {
      setExpanded(!expanded);
    }

    if (!canCloseAccordion && !expanded) {
      setExpanded(true);
    }
  };

  useEffect(() => {
    if (isEventBusCreated()) {
      window.LIFE_CYCLE_EVENT_BUS.on('product-section-key-feat.click', ({ output = true }) => {
        handleAccordionChange(output);
      });
    }
  }, []);

  const renderAccordionBody = () => {
    if (withKeyProductFeatures) {
      return (
        <SpecificationsWithKPFAccordionBodyComponent
          itemId={itemId}
          oneColumn={oneColumn}
          returnableStatus={returnableStatus}
          isWhiteSpecTitle={isWhiteSpecTitle}
          displaySimilarLinks={displaySimilarLinks}
        />
      );
    }

    return (
      <SpecificationsAccordionBodyComponent
        itemId={itemId}
        oneColumn={oneColumn}
        returnableStatus={returnableStatus}
        isWhiteSpecTitle={isWhiteSpecTitle}
      />
    );
  };

  return (
    <Accordion
      key="specifications-accordion"
      defaultExpanded={thdSeoBotDetected}
      expanded={expanded}
      onChange={() => handleAccordionChange()}
    >
      <AccordionHeader>
        <div className="sui-flex sui-flex-col">
          <SpecificationsAccordionTitle itemId={itemId} showSubtitle={showSubtitle} />
        </div>
      </AccordionHeader>
      <AccordionBody>
        {renderAccordionBody()}
      </AccordionBody>
    </Accordion>
  );
};

SpecificationsAccordionComponent.displayName = 'SpecificationsAccordion';

SpecificationsAccordionComponent.dataModel = dataModel;

const propTypes = {
  displaySimilarLinks: PropTypes.bool,
  /** SKU Number */
  itemId: PropTypes.string.isRequired,
  returnableStatus: PropTypes.bool,
  oneColumn: PropTypes.bool,
  isWhiteSpecTitle: PropTypes.bool,
  showSubtitle: PropTypes.bool,
  withKeyProductFeatures: PropTypes.bool
};

const defaultProps = {
  displaySimilarLinks: true,
  oneColumn: false,
  returnableStatus: false,
  isWhiteSpecTitle: false,
  showSubtitle: true,
  withKeyProductFeatures: false
};

SpecificationsAccordionComponent.propTypes = propTypes;
SpecificationsAccordionComponent.defaultProps = defaultProps;

const HydratedComponent = withHydrator({
  delay: 200,
  pageType: 'pip',
  id: 'product-section-key-feat',
  placeholder: (
    <Skeleton className="sui-w-full">
      <SkeletonBlock height={52} />
    </Skeleton>
  ),
  scrollBuffer: 900,
}, SpecificationsAccordionComponent);

const DynamicComponent = withDynamicComponent(HydratedComponent);
const SpecificationsAccordion = withErrorBoundary(DynamicComponent);

SpecificationsAccordion.displayName = 'SpecificationsAccordion';

SpecificationsAccordion.dataModel = dataModel;
SpecificationsAccordion.propTypes = propTypes;
SpecificationsAccordion.defaultProps = defaultProps;

export { SpecificationsAccordion };